import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../layoutV2/DashboardLayout';
import { getCustomerDetails } from '../services/CustomerService';
import { getUserDetails } from '../services/UserDetailsService';
import { formatPhoneNumber } from '../utils/utils';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [userDetails, setUserDetails] = useState({});
  const [propertyDetails, setPropertyDetails] = useState({});
  const [siteVisitData, setSiteVisitData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [installationTimeline, setInstallationTimeline] = useState('1 month');

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { id } = getUserDetails();
        const userDetails = await getCustomerDetails(id);
        setUserDetails(userDetails);
        setPropertyDetails(userDetails?.propertyDetails[0]);
        setSiteVisitData(userDetails?.propertyDetails[0]?.siteVisitData);
        setInstallationTimeline(
          userDetails?.propertyDetails[0]?.installationTimeline,
        );
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserDetails();
  }, []);

  const handleAutoAccess = () => {
    const passkey = userDetails?.metadata?.passkey;
    const decodedId = userDetails?.metadata?.applicationId;
    if (!passkey || !decodedId) {
      alert('No passkey or application ID found.');
      return;
    }
    localStorage.setItem('myPasskey', passkey);
    navigate('/en/shareableapplication/' + decodedId);
  };

  const isVerified = propertyDetails?.verificationStatus === 'VERIFIED';

  const openInNewTab = (url) => {
    window.open(url, '_blank');
  };

  return (
    <DashboardLayout
      announcementBar={true}
      propertyDetails={propertyDetails}
      installationTimeline={installationTimeline}
    >
      <Box sx={{ p: 3 }}>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='h4' gutterBottom sx={{ color: 'green' }}>
                Hi, {userDetails.fullName || 'N/A'}
              </Typography>
              <Button
                variant='outlined'
                sx={{ mb: 2, textTransform: 'none' }}
                onClick={handleAutoAccess}
              >
                Application status
              </Button>
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    boxShadow: 'none',
                    border: '1px solid lightgray',
                  }}
                >
                  <Typography variant='h6' gutterBottom sx={{ color: 'green' }}>
                    Personal Information
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    <strong>Name:</strong> {userDetails?.fullName || 'N/A'}
                  </Typography>
                  <Typography variant='body1' gutterBottom>
                    <strong>Phone Number:</strong>{' '}
                    {formatPhoneNumber(userDetails?.phoneNumber)}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    boxShadow: 'none',
                    border: '1px solid lightgray',
                  }}
                >
                  <Typography variant='h6' gutterBottom sx={{ color: 'green' }}>
                    Property Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>PinCode:</strong>{' '}
                        {propertyDetails?.pinCode || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Roof top Area (Shadow Free):</strong>{' '}
                        {propertyDetails?.shadowFreeArea
                          ? `${propertyDetails.shadowFreeArea} sqft`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Bill Amount:</strong>{' '}
                        {propertyDetails?.billAmount
                          ? `₹${propertyDetails.billAmount}`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Bill Month:</strong>{' '}
                        {propertyDetails?.billMonth || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Sanctioned Load:</strong>{' '}
                        {propertyDetails?.sanctionedLoad
                          ? `${propertyDetails.sanctionedLoad} KW`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Electricity Bill URL:</strong>
                        {propertyDetails?.electricityBillUrl ? (
                          <a
                            href={propertyDetails?.electricityBillUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            View Bill
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack direction={'row'} gap={'0.5rem'}>
                        <Typography variant='body1' gutterBottom>
                          <strong>Verification Status:</strong>
                        </Typography>
                        {isVerified ? (
                          <CheckCircleIcon color='success' />
                        ) : (
                          <CancelIcon color='error' />
                        )}
                      </Stack>
                    </Grid>

                    <Stack
                      direction={isMobile ? 'column' : 'row'}
                      justifyContent={'flex-end'}
                      pt={2}
                      width={'100%'}
                    >
                      <Button
                        variant='contained'
                        disableElevation
                        startIcon={<EditIcon />}
                        sx={{ textTransform: 'none' }}
                        size={isMobile ? 'small' : 'default'}
                        onClick={() => openInNewTab('/start')}
                      >
                        Update
                      </Button>
                    </Stack>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    boxShadow: 'none',
                    border: '1px solid lightgray',
                  }}
                >
                  <Typography variant='h6' gutterBottom sx={{ color: 'green' }}>
                    Site Visit Data
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Floor:</strong> {siteVisitData?.floor || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Roof Type:</strong>{' '}
                        {siteVisitData?.roofType || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Roof Usage:</strong>{' '}
                        {siteVisitData?.roofUsage || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Roof Top Area:</strong>{' '}
                        {siteVisitData?.roofTopArea
                          ? `${siteVisitData.roofTopArea} sqft`
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant='body1' gutterBottom>
                        <strong>Power Cut Frequency:</strong>{' '}
                        {siteVisitData?.powerCutFrequency || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    justifyContent={'flex-end'}
                    pt={2}
                    width={'100%'}
                  >
                    <Button
                      variant='contained'
                      disableElevation
                      sx={{ textTransform: 'none' }}
                      size={isMobile ? 'small' : 'default'}
                      startIcon={<EditIcon />}
                      onClick={() =>
                        openInNewTab(
                          `/site-details?id=${propertyDetails?.id}&step=1`,
                        )
                      }
                    >
                      Add site info
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ProfilePage;
